import { Card, CardContent, CardHeader, Typography, Checkbox, FormControlLabel, Box, colors, Alert, Button, Tooltip } from "@mui/material";
import { useState, useEffect } from 'react';
import { useHqFansAPI } from "../../fans-list/hq-fans.api";
import { CardSearch } from "@arelia-components/ui-atoms/CardSearch";
import SpinnerWrapper from "@arelia-components/ui-atoms/SpinnerWrapper";
import { escapeRegExp } from "lodash";
import { FormEditActions } from "@arelia-components/form/FormEditActions";
import { toast } from "sonner";
import { useTwilioConfigAPI } from "../../settings/settings.api";
import { useNavigate } from "react-router-dom";
import { PATHS } from "src/routes";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import { parsePhoneNumberFromString } from "libphonenumber-js";

type Props = {
  checkedFans: string[];
  saveCheckedFans: (checkedFans: string[]) => void;
};

function isValidPhone(raw?: string | null): boolean {
  if (!raw)
    return false;

  try {
    const pn = parsePhoneNumberFromString(String(raw), "US");
    if (!pn) 
      return false;
    
    return pn.isValid() && pn.country === "US";
  } catch {
    return false;
  }
}

function CheckFansListFilters({ searchValue, setSearchValue, onSelectAll, onDeselectAll, selectAllChecked }: {
  searchValue: string;
  setSearchValue: (value: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
  selectAllChecked: boolean;
}) {
  return <Box sx={styles.fanListFiltersBox}>
    <CardSearch value={searchValue} onChange={setSearchValue} customStyles={styles.cardSearch} />
    <FormControlLabel
      control={
        <Checkbox
          checked={selectAllChecked}
          onChange={(e) => {
            if (e.target.checked) {
              onSelectAll();
            } else {
              onDeselectAll();
            }
          }}
        />
      }
      label={
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <Typography component="span">Select all</Typography>
          <Tooltip title="Only fans with valid US phone numbers are presented in the list" placement="top">
            <InfoOutlined
              fontSize="small"
              sx={{ color: colors.grey[600] }}
              onClick={(e) => e.stopPropagation()}
            />
          </Tooltip>
        </Box>
      }
    />
  </Box>
}

export default function CheckFansList({ checkedFans, saveCheckedFans }: Props) {
  const { data, isLoading } = useHqFansAPI();
  const fans = (data ?? []).filter(f => isValidPhone(f.phone));
  const [checkedFanIds, setCheckedFanIds] = useState<string[]>(checkedFans);
  const [searchValue, setSearchValue] = useState('');
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();

  const { data: twilioConfig } = useTwilioConfigAPI();
  const maxSelectable = (twilioConfig?.messageLimitCurrent ?? 0) + (twilioConfig?.additionalMessageLimit ?? 0);

  useEffect(() => {
    setCheckedFanIds(checkedFans);
  }, [checkedFans]);

  const handleCheckboxChange = (fanId: string) => {
    if (!checkedFanIds.includes(fanId)) {
      if (maxSelectable !== undefined && checkedFanIds.length >= maxSelectable) {
        toast.warning(`You cannot select more fans than your available messages (max ${maxSelectable}).`);
        return;
      }
      setCheckedFanIds([...checkedFanIds, fanId]);
    } else {
      setCheckedFanIds(checkedFanIds.filter(id => id !== fanId));
    }
  };

  const handleEdit = () => {
    setEditing(true);
  };

  const handleSave = () => {
    setEditing(false);
    const validIds = checkedFanIds.filter(id => fans.some(f => f.id === id));
    saveCheckedFans(validIds);
  };

  const handleReset = () => {
    setEditing(false);
    setCheckedFanIds(checkedFans);
  };

  const selectAll = () => {
    const selectedSet = new Set(checkedFanIds);
    const newSelection = [...checkedFanIds];

    for (const fan of fans) {
      if (selectedSet.size >= maxSelectable) break;
      if (!selectedSet.has(fan.id)) {
        newSelection.push(fan.id);
        selectedSet.add(fan.id);
      }
    }

    if (selectedSet.size < fans.length) {
      toast.warning(`Only ${maxSelectable} fans can be selected because of your available messages.`);
    }

    setCheckedFanIds(newSelection);
  };

  const deselectAll = () => {
    setCheckedFanIds([]);
  };

  const searchPattern = new RegExp(escapeRegExp(searchValue), "i");
  const filteredFans = fans.filter(fan =>
    searchPattern.test(fan.fullName as string) || searchPattern.test(fan.city as string)
  );

  const hasNoFans = fans.length === 0;

  const editingContent =
    <CardContent sx={styles.cardContent}>
      <CheckFansListFilters
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        onSelectAll={selectAll} onDeselectAll={deselectAll}
        selectAllChecked={checkedFanIds.length > 0 && checkedFanIds.length === fans.length}
      />
      <Box sx={styles.fanList}>
        {filteredFans.map((fan) => (
          <Box key={fan.id} sx={styles.editFanRow}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={checkedFanIds.includes(fan.id)}
                  onChange={() => handleCheckboxChange(fan.id)}
                />
              }
              label={
                <Typography sx={{ textTransform: 'capitalize' }}>
                  {fan.fullName} ({fan.city})
                </Typography>
              }
            />
          </Box>
        ))}
      </Box>
      <Box sx={styles.footer} />
    </CardContent>;

  const readonlyContent =
    <CardContent sx={styles.cardContent}>
      <Box sx={styles.fanList}>
        {checkedFanIds?.length === 0
          ? (
            <Box sx={styles.noFansContainer}>
              <Box>
                <p>No fans selected.</p>
                <Alert severity="info" variant="filled" sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex' }}>
                    {hasNoFans ? (
                      <Typography> <span style={{ fontWeight: 'bold' }}>STEP 1: </span>
                        Your fans list is empty. Add new records by clicking button below.</Typography>
                    ) : (
                      <Typography> <span style={{ fontWeight: 'bold' }}>STEP 1: </span>
                        Select fans who will receive campaign SMS message</Typography>
                    )}
                  </Box>
                </Alert>
              </Box>
              <Box sx={styles.selectFansButtonContainer}>
                {hasNoFans ? (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate(PATHS.FANS_LIST)}
                    sx={styles.selectFansButton}
                  >
                    Add Fans
                  </Button>
                ) : (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={handleEdit}
                    sx={styles.selectFansButton}
                  >
                    Select Fans
                  </Button>
                )}
              </Box>
            </Box>
          )
          : checkedFanIds.map((fanId) => {
            const fan = fans.find(fan => fan.id === fanId);
            return (
              <Box
                key={fanId}
                sx={{
                  ...styles.fanRow
                }}
              >
                <Typography sx={{ textTransform: 'capitalize' }}>
                  {fan?.fullName}
                </Typography>
                <Typography sx={{ textTransform: 'capitalize' }}>
                  {fan?.city}
                </Typography>
              </Box>
            );
          })}
      </Box>
    </CardContent>

  const cardTitle = editing ? "Edit Campaign Fans" : "Campaign Fans";

  return (
    <Card sx={styles.card}>
      <CardHeader title={cardTitle}
        action={<FormEditActions
          disabled={isLoading}
          edit={editing}
          handleEdit={handleEdit}
          handleReset={handleReset}
          handleSubmit={handleSave} />}
      />
      <SpinnerWrapper loading={isLoading}>
        {editing ? editingContent : readonlyContent}
      </SpinnerWrapper>
    </Card>
  );
}

const styles = {
  cardSearch: {
    mt: 1,
    mr: 1,
    width: "250px",
    margin: '0'
  },
  card: {
    minHeight: "450px",
    maxHeight: "450px",
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minWidth: '400px',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    paddingBottom: '16px',
  },
  fanList: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingTop: '4px'
  },
  editFanRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  },
  fanRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    '&:not(:last-child)': {
      borderBottom: '1px solid #eee',
      pb: 1,
      mb: 1,
    }
  },
  fanListFiltersBox: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    borderBottom: 'solid 1px',
    borderBottomColor: colors.grey[300],
    paddingBottom: '8px'
  },
  footer: {
    padding: '16px'
  },
  noFansContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%'
  },
  selectFansButtonContainer: {
    flex: 0.8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  selectFansButton: {
    borderRadius: '20px',
    px: 4,
    py: 1,
    fontSize: '1rem',
    minWidth: '180px'
  }
}
